import type { AiSummaryFieldConfig } from '@databases/types/database';

type AiSummaryPromptContext = {
  targetFieldName: string;
  config: AiSummaryFieldConfig;
};

export type AiSummaryFieldRunOperation =
  | 'initial-generation'
  | 'refresh-entire-column'
  | 'refresh-empty-entries'
  | 'single-entry-generate'
  | 'multiple-entry-generate';

type AiSummaryPromptBuilder = (context: AiSummaryPromptContext) => string;

const buildSharedGenerateInstructions = (targetFieldName: string) => [
  `If an included row has no meaningful information, leave the "${targetFieldName}" value blank.`,
  `Only edit the "${targetFieldName}" field. Do not emit add_field metadata or create, remove, rename, or reconfigure fields; add or remove rows; edit views; or change any other field values.`,
  'Return the edits using the traditional AIFC Confluence database update CSV response format.',
  `CRITICAL: Ensure that the summary generated for ${targetFieldName} is wrapped in quotes and is a valid string value.`
];

const buildInitialGenerationPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Canvas has already created the new AI Summary field named "${targetFieldName}" and included it in the database context as an existing column. It is expected to be empty.`,
    `Populate that exact existing "${targetFieldName}" column for every existing row. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildRefreshEntireColumnPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Regenerate the existing AI Summary field named "${targetFieldName}". It is already present in the database context and may contain existing values.`,
    `Replace only that exact "${targetFieldName}" column's value for every existing row, including rows that already have a value. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildRefreshEmptyEntriesPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Fill the existing AI Summary field named "${targetFieldName}" only for the provided rows, where its current value is empty.`,
    `Do not replace any existing "${targetFieldName}" values. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildSingleEntryGeneratePrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Edit only the existing "${targetFieldName}" AI Summary value for the single row included in the database context.`,
    `For the included row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName),
    'Use exactly one edit_entry operation with the existing entry_index value.'
  ].join('\n\n');

const buildMultipleEntryGeneratePrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Edit only the existing "${targetFieldName}" AI Summary values for the rows included in the database context.`,
    `For each included row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName),
    'Use one edit_entry operation for each included row, using its existing entry_index value.'
  ].join('\n\n');

const promptBuilders = {
  'initial-generation': buildInitialGenerationPrompt,
  'refresh-entire-column': buildRefreshEntireColumnPrompt,
  'refresh-empty-entries': buildRefreshEmptyEntriesPrompt,
  'single-entry-generate': buildSingleEntryGeneratePrompt,
  'multiple-entry-generate': buildMultipleEntryGeneratePrompt
} satisfies Record<AiSummaryFieldRunOperation, AiSummaryPromptBuilder>;

export const buildAiSummaryPrompt = ({
  operation,
  targetFieldName,
  config
}: AiSummaryPromptContext & { operation: AiSummaryFieldRunOperation }): string =>
  promptBuilders[operation]({ targetFieldName, config });
